import React, { useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { useNavigate, useLocation, useLoaderData, useParams } from 'react-router-dom';
import { LearningPathItem, LearningSidebar } from '@atlassian/learning-components';
import { media } from '@atlaskit/primitives/responsive';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { sendUIEvent } from 'src/utils/analytics';
import { SUBNAVBAR_HEIGHT } from 'src/pages/LessonPage/utils/utils';
import { getSlug } from 'src/utils/slug';
import { useProgressState } from 'src/store/progress/progressStore';
import { LessonLoaderProps } from 'src/pages/LessonPage';
import { getLessonUrl } from 'src/utils/getUrl';

import { scrollToSection, addQuizToLessons } from './utils/sidebarUtils';
import { CoursePopup } from './CoursePopup';

export interface SidebarProps {
  onChangeCurrentUrl: (url: string) => void;
  isSidebarVisible: boolean;
  onChangeSidebarVisibility: (visible: boolean) => void;
}

export const TOP_NAVBAR_HEIGHT = 112;

export const Sidebar: React.FC<SidebarProps> = ({
  onChangeCurrentUrl,
  isSidebarVisible,
  onChangeSidebarVisibility,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const { course, lesson, learningPath } = useLoaderData() as LessonLoaderProps;

  const [expandedCourseIds, setExpandedCourseIds] = useState<string[]>([]);
  const [expandedLessonIds, setExpandedLessonIds] = useState<string[]>([]);

  const { user, isLoading } = useAtlassianUser();

  const isNextLessonNavigation = location.state?.isNextLessonNavigation ?? false;

  const [{ progressForLessons, progressForCourses }] = useProgressState();

  const isCourse = !learningPath;

  const [topSpacing, setTopSpacing] = useState(0);

  const courseSlug = getSlug(course);
  const lessonSlug = getSlug(lesson);

  useEffect(() => {
    const handleScroll = (): void => {
      setTopSpacing(Math.min(window.scrollY, SUBNAVBAR_HEIGHT));
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const dynamicStyles = {
    transform: `translateY(-${topSpacing}px)`,
    height: `calc(100vh - ${136 - topSpacing}px)`,
  };

  useEffect(() => {
    onChangeCurrentUrl(globalThis.location.href);
  }, [location, onChangeCurrentUrl]);

  useEffect(() => {
    const courseId = params.courseId;
    const lessonId = params.lessonId;

    const selectedCourseId = isCourse
      ? course?.sys.id
      : learningPath?.coursesCollection.items.find((course) => courseId === course.slug)?.sys.id || '';

    const selectedLessonId = isCourse
      ? course?.lessonsCollection.items.find((lesson) => lessonId === lesson.slug)?.sys.id || ''
      : learningPath?.coursesCollection.items
        .find((course) => courseId === course.slug)
        ?.lessonsCollection.items.find((lesson) => lessonId === lesson.slug)?.sys.id || '';

    const shouldTriggerExpand =
      isNextLessonNavigation ||
      (expandedCourseIds.length === 0 && expandedLessonIds.length === 0) ||
      !expandedCourseIds.includes(selectedCourseId || '');

    if (shouldTriggerExpand) {
      if (selectedCourseId) {
        setExpandedCourseIds([selectedCourseId]);
      }
      if (selectedLessonId) {
        setExpandedLessonIds([selectedLessonId]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCourse,
    isNextLessonNavigation,
    location,
    course,
    expandedCourseIds.length,
    expandedLessonIds.length,
    params.courseId,
    courseSlug,
  ]);

  const handleSelect = (courseSlug: string, lessonSlug?: string, sectionSlug?: string): void => {
    if (!lessonSlug) {
      return;
    }

    const lessonUrl = getLessonUrl({
      lessonSlug,
      courseSlug,
      pathSlug: isCourse ? undefined : getSlug(learningPath),
      sectionSlug,
    });

    if (sectionSlug) {
      scrollToSection(sectionSlug);
    }

    navigate(lessonUrl);
  };

  const handlePopupClick = (slug: string, title: string): void => {
    sendUIEvent(
      'lessonScreen',
      'popup',
      'clicked',
      'sidebarCoursePopup',
      {
        currentCourseSlug: courseSlug,
        lessonSlug,
        redirectCourseSlug: slug,
        redirectCourseTitle: title,
      },
      user?.account_id,
    );
  };

  const learningSidebarItem: LearningPathItem = {
    title: learningPath?.title,
    slug: learningPath?.slug ?? '',
    coursesCollection: {
      items: isCourse
        ? [addQuizToLessons(course!)]
        : learningPath?.coursesCollection?.items?.map(addQuizToLessons) || [],
    },
  };

  return (
    <div
      css={[
        sidebarContainerStyles,
        !isSidebarVisible && sidebarContainerClosedStyles,
        isSidebarVisible && sidebarContainerOpenedStyles,
        disableDynamicStylesOnTable,
      ]}
      style={dynamicStyles}
    >
      <LearningSidebar
        isLoading={isLoading}
        learningPath={learningSidebarItem}
        isVisible={isSidebarVisible}
        progressForLessons={progressForLessons}
        progressForCourses={progressForCourses}
        expandedCourseIds={expandedCourseIds}
        expandedLessonIds={expandedLessonIds}
        onSelect={handleSelect}
        onToggleVisibility={() => onChangeSidebarVisibility(!isSidebarVisible)}
        navigate={navigate}
        renderPopup={(course) => (
          <CoursePopup course={course} pathSlug={getSlug(learningPath)} handlePopupClick={handlePopupClick} />
        )}
      />
    </div>
  );
};

const disableDynamicStylesOnTable = css({
  '@media (max-width: 1279px)': {
    transform: 'unset !important',
    top: '56px !important',
    height: 'calc(100vh - 56px) !important',
  },
});

const sidebarContainerStyles = css({
  transition: 'top 0.3s',
  zIndex: 5,
  position: 'fixed',
  top: '136px',

  '@media (max-width: 576px)': {
    height: 'calc(100vh - 104px)',
  },
});

const sidebarContainerClosedStyles = css({
  width: 0,

  [media.below.xs]: {
    width: '100%',
    transform: 'translateX(-100%) !important',
  },
});

const sidebarContainerOpenedStyles = css({
  [media.below.xs]: {
    width: '100%',
  },
});
